// Regras de negócio de Produto/Complemento/Subitem/Grade, portadas do sz-foods
// (app/_context/cart.tsx + app/products/[id]/_components/product-details.tsx).
// Mantidas em JS puro e sem estado — só recebem dados e devolvem resultado, pra poder
// ser reaproveitadas em qualquer tela do Portal que precise montar um item de venda.

/**
 * Gera a chave única do item no "carrinho" local. Produtos idênticos (mesmo produto,
 * mesmos complementos com mesma quantidade, mesma observação) usam a mesma chave e
 * devem agregar quantidade em vez de virar linha nova.
 * Produto com grade selecionada NUNCA agrega — a chave sempre sai única (anti-agregação).
 */
export function buildCartKey(idProduto, complementosSelecionados = [], obs) {
	const ids = complementosSelecionados
		.map((c) => `${c.id_complementos_item}x${c.quantidade}`)
		.sort()
		.join("_");

	let baseKey = ids ? `${idProduto}_${ids}` : `${idProduto}`;

	if (obs) {
		// Hash curto da observação, só pra garantir unicidade sem inchar a chave
		let hash = 0;
		for (let i = 0; i < obs.length; i++) {
			hash = (hash << 5) - hash + obs.charCodeAt(i);
			hash |= 0;
		}
		baseKey += `_obs_${Math.abs(hash).toString(36)}`;
	}

	const hasGrade = complementosSelecionados.some((c) => c.is_grade);
	if (hasGrade) {
		baseKey += `_grade_${Math.random().toString(36).substring(2, 10)}`;
	}

	return baseKey;
}

/**
 * Calcula o preço final do produto a partir dos complementos selecionados.
 *
 * Regras:
 * - Normal: soma normalmente sobre o preço de tabela.
 * - Subitem: o MAIOR valor selecionado no grupo SUBSTITUI o preço base (não soma).
 *   Se houver mais de um grupo de subitem, soma-se o maior de cada grupo.
 * - Grade: o preço base do produto pai é forçado a ZERO; o valor da grade escolhida
 *   soma normalmente (não sofre o "zeramento" que o subitem sofre).
 *
 * Espelha exatamente a lógica de handleAddToCartClick em
 * sz-foods/app/products/[id]/_components/product-details.tsx.
 */
export function calcularPrecoProduto({ valorTabela, valorPromo = 0, complementosSelecionados = [], hasGrade = false }) {
	const grupos = new Map();
	complementosSelecionados.forEach((c) => {
		const g = grupos.get(c.id_complementos_grupo) || [];
		g.push(c);
		grupos.set(c.id_complementos_grupo, g);
	});

	let totalSubitems = 0;
	let totalNormais = 0;
	let temSubitem = false;

	grupos.forEach((itens) => {
		const isSubitem = itens.some((i) => i.subitem);
		if (isSubitem) {
			temSubitem = true;
			totalSubitems += Math.max(...itens.map((i) => Number(i.valor_item) || 0));
		} else {
			totalNormais += itens.reduce((acc, i) => acc + (Number(i.valor_total) || 0), 0);
		}
	});

	// Se houver valor promocional válido na tabela de preços do produto
	const temPromoValida = Number(valorPromo) > 0 && Number(valorPromo) < Number(valorTabela);
	const precoTabelaEfetivo = temPromoValida ? Number(valorPromo) : Number(valorTabela);

	const precoBasePai = hasGrade ? 0 : precoTabelaEfetivo;
	const precoBaseEfetivo = temSubitem ? totalSubitems : precoBasePai;
	const valorFinal = precoBaseEfetivo + totalNormais;

	// Valor "de tabela" a exibir/gravar como referência (usado p.ex. em cálculo de desconto)
	let valorOriginal;
	if (hasGrade) {
		valorOriginal = 0;
	} else if (temSubitem) {
		valorOriginal = precoBaseEfetivo;
	} else {
		valorOriginal = precoBaseEfetivo;
	}

	// Valor do desconto unitário promocional do item pai
	const descontoUnitario = temPromoValida && !hasGrade && !temSubitem ? (Number(valorTabela) - Number(valorPromo)) : 0;

	// Zera os subitens antes de gravar/levar pro carrinho — o valor já foi incorporado
	// no preço base (evita cobrar em duplicidade). Grade NÃO é zerada.
	const complementosFinais = complementosSelecionados.map((c) =>
		c.subitem ? { ...c, valor_item: 0, valor_total: 0 } : c,
	);

	return {
		temSubitem,
		valorOriginal,
		valorFinal,
		descontoUnitario,
		complementosFinais,
	};
}

/**
 * Verifica se todos os grupos obrigatórios têm pelo menos 1 item selecionado.
 */
export function validarGruposObrigatorios(grupos = [], complementosSelecionados = []) {
	return grupos.every((group) => {
		if (!group.obrigatorio) return true;
		return complementosSelecionados.some((s) => s.id_complementos_grupo === group.id);
	});
}
